import { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, MenuItem, Select, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useAreaInfo, useImageryOptions } from "../../api/dataReview";
import { useDataReview } from "../../contexts/DataReviewContext";
import { DrawingContext, DrawingMode } from "../../contexts/DrawingContext";
import { AMBER, FAINT, LINE, MONO, MUTED, TEAL, TEXT, btnPlain, btnPrimary, capSx, cardSx, floatSx, inputSx, menuItemSx, pillSx, radioSx } from "./editorTheme";
import { bboxOfGeometry } from "../dataReview/geometry";
import { fmtArea, geometryAreaM2 } from "./editorModel";
import { useEditorData } from "./useEditorData";
import { useEditorActions } from "./useEditorActions";
import { useVegEditor } from "./VegEditorContext";

/** "Jan 2021" from a photo's recorded capture date. */
const photoDate = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", year: "numeric" }); };

/** Setting up a refinement: which area, whether to start from a clean
 *  slate, which image. Starting reads the image; findings and paint come
 *  after. */
export default function VegEditorSetup() {
  const d = useEditorData();
  const a = useEditorActions();
  const { setup, patchSetup } = useVegEditor();
  const { pickedImagery, setPickedImagery, setTracedArea } = useDataReview();
  const { drawingMode } = useContext(DrawingContext);
  const area = setup.areaMode === "project" ? d.boundary : setup.drawPoly;
  const areaM2 = useMemo(() => geometryAreaM2(area?.geometry), [area]);
  const drawing = drawingMode === DrawingMode.DATA_AREA;
  // An imagery run reads the photo over the whole area, so it is capped
  // (painting, which needs no image, never comes through here).
  const tooBig = areaM2 > d.maxAreaM2;

  // The chosen area is outlined (and everything else dimmed) while choosing.
  useEffect(() => { setTracedArea(area ?? null); return () => setTracedArea(null); }, [area, setTracedArea]);

  const bbox = useMemo(() => {
    const b = bboxOfGeometry(area?.geometry);
    return b ? { west: b[0][0], south: b[0][1], east: b[1][0], north: b[1][1] } : null;
  }, [area]);
  const { data: imageryRes, isLoading: imageryLoading } = useImageryOptions(d.projectId, bbox);
  const options = imageryRes?.data ?? [];
  useEffect(() => {
    if (options.length && !options.some((o) => o.key === pickedImagery?.key)) setPickedImagery(options[0]);
  }, [options, pickedImagery, setPickedImagery]);

  const areaInfo = useAreaInfo(d.projectId);
  const [info, setInfo] = useState<{ buildings: number } | null>(null);
  useEffect(() => {
    setInfo(null);
    if (!area) return;
    areaInfo.mutate(area, { onSuccess: (r) => setInfo(r.data ?? null) });
  }, [area]);

  // Recommend a clean slate when one class blankets the project: there is
  // little worth keeping, and finds plus paint rebuild it faster.
  const legend = d.vegArea?.class_areas ?? [];
  const total = legend.reduce((s, c) => s + c.area_ha, 0);
  const dominant = legend[0];
  const share = dominant && total ? dominant.area_ha / total : 0;
  const recClean = share >= 0.85;

  const chooseDrawn = () => { patchSetup({ areaMode: "draw", drawPoly: null }); a.startDrawing("area"); };
  const chooseProject = () => { a.stopDrawing(); patchSetup({ areaMode: "project" }); };
  return (
    <Box sx={{ ...floatSx, position: "fixed", top: "calc(var(--topbar-h) + var(--strategies-h) + 12px)", left: 12, width: 372, maxHeight: "calc(var(--vh100) - var(--topbar-h) - var(--strategies-h) - 24px)", display: "flex", flexDirection: "column", zIndex: 1100 }}>
      <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Button onClick={a.goHome} sx={{ textTransform: "none", color: MUTED, fontSize: 11.5, p: 0, minWidth: 0, "&:hover": { color: TEXT, bgcolor: "transparent" } }} startIcon={<ChevronLeftIcon sx={{ fontSize: 14, mr: -0.75 }} />}>Vegetation</Button>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 1 }}>New refinement</Typography>

        <Typography sx={{ ...capSx, mt: 2, mb: 1 }}>Area</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={cardSx(setup.areaMode === "project")} onClick={chooseProject}>
            <Box sx={radioSx(setup.areaMode === "project")} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Whole project</Typography>
              <Typography sx={{ fontSize: 12, color: MUTED }}>Everything inside the boundary</Typography>
            </Box>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{fmtArea(d.projectAreaM2)}</Typography>
          </Box>
          <Box sx={cardSx(setup.areaMode === "draw")} onClick={chooseDrawn}>
            <Box sx={radioSx(setup.areaMode === "draw")} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Drawn area</Typography>
              <Typography sx={{ fontSize: 12, color: setup.areaMode === "draw" && !setup.drawPoly ? AMBER : MUTED }}>
                {setup.areaMode === "draw" && !setup.drawPoly ? (drawing ? "Click around the area, then press Enter or the tick." : "Trace it on the map") : "Trace it on the map"}
              </Typography>
            </Box>
            {setup.drawPoly && <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{fmtArea(areaM2)}</Typography>}
          </Box>
        </Box>
        {tooBig && (
          <Typography sx={{ fontSize: 12, color: AMBER, mt: 1, lineHeight: 1.5 }}>
            {fmtArea(areaM2)} is more than one imagery run can read ({fmtArea(d.maxAreaM2)}). Trace a smaller area.
          </Typography>
        )}

        <Typography sx={{ ...capSx, mt: 2.25, mb: 1 }}>Starting point</Typography>
        <Box sx={cardSx(setup.startMode === "clean")} onClick={() => patchSetup({ startMode: "clean" })}>
          <Box sx={radioSx(setup.startMode === "clean")} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Start from a clean slate</Typography>
              {recClean && <Box sx={pillSx("ok")}>Recommended</Box>}
            </Box>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>Clear the vegetation inside the area, then build it back from what you find and paint.</Typography>
          </Box>
        </Box>
        {setup.startMode === "clean" && (
          <Box component="label" sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, m: "8px 0 2px 28px", cursor: "pointer" }}>
            <Checkbox size="small" checked={setup.keepBuildings} onChange={(ev) => patchSetup({ keepBuildings: ev.target.checked })} sx={{ p: 0.25, color: MUTED, "&.Mui-checked": { color: TEAL } }} />
            <Typography sx={{ fontSize: 12, mt: 0.4 }}>
              Keep the <strong>{info ? info.buildings : "…"}</strong> buildings etched at project creation <span style={{ color: MUTED }}>— they stay locked while you work</span>
            </Typography>
          </Box>
        )}
        <Box sx={{ ...cardSx(setup.startMode === "build"), mt: 1 }} onClick={() => patchSetup({ startMode: "build" })}>
          <Box sx={radioSx(setup.startMode === "build")} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Build on the current layer</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>Shapes you accept replace what's underneath. Everything else stays as it is.</Typography>
          </Box>
        </Box>
        {recClean && dominant && (
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 1.25, lineHeight: 1.5 }}>
            Recommended because {share >= 0.999 ? "all" : `${Math.round(share * 100)}%`} of the vegetation is one class ({dominant.veg_class}), so there is little worth keeping.
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 2.25, p: "8px 12px", borderRadius: 2, bgcolor: "rgba(255,255,255,0.04)", border: `1px solid ${LINE}` }}>
          <Typography sx={{ fontSize: 12, color: MUTED, flex: "none" }}>Image</Typography>
          {imageryLoading ? <CircularProgress size={14} sx={{ color: TEAL }} /> : options.length ? (
            <Select size="small" value={pickedImagery?.key ?? options[0].key} onChange={(ev) => setPickedImagery(options.find((o) => o.key === ev.target.value) ?? null)}
              sx={{ ...inputSx, flex: 1, height: 30 }} MenuProps={{ PaperProps: { sx: { bgcolor: "#1d2733", color: TEXT } } }}>
              {options.map((o) => <MenuItem key={o.key} value={o.key} sx={menuItemSx}>{o.name} · {o.gsd_m} m{o.captured ? ` · ${photoDate(o.captured)}` : ""}</MenuItem>)}
            </Select>
          ) : <Typography sx={{ fontSize: 12, color: AMBER }}>{area ? "No photo covers this area." : "Choose an area first."}</Typography>}
        </Box>
        <Typography sx={{ fontSize: 12, color: FAINT, mt: 1.5, lineHeight: 1.5 }}>
          The image takes a few minutes to prepare. You can start asking for things straight away.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, p: "12px 16px", borderTop: `1px solid ${LINE}` }}>
        <Button sx={btnPlain} onClick={a.goHome}>Cancel</Button>
        <Button sx={{ ...btnPrimary, flex: 1 }} disabled={!area || !pickedImagery || tooBig || a.busy.start || (setup.areaMode === "draw" && !setup.drawPoly)}
          onClick={() => a.start(pickedImagery?.key, area)}>
          {a.busy.start ? "Starting…" : setup.startMode === "clean" ? "Clear area and start" : "Start"}
        </Button>
      </Box>
    </Box>
  );
}
